/**
 * Main Layout Component
 * Application layout wrapper with sidebar, header, and content area
 */

import { useEffect } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { Sidebar } from '@/components/common/layout/sidebar';
import { Header } from '@/components/common/layout/header';
import { PageWrapper } from '@/components/common/layout/page-wrapper';
import { useRequireAuth } from '@/hooks/use-auth';
import { useUIStore } from '@/stores/ui-store';
import { useAuthStore } from '@/stores';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Toaster } from '@/components/ui/toaster';

interface MainLayoutProps {
  children?: React.ReactNode;
  requireAdmin?: boolean;
}

export default function MainLayout({ children, requireAdmin = false }: MainLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { isAuthenticated, isInitialized } = useRequireAuth();
  const { setPageTitle, setBreadcrumbs } = useUIStore();
  
  // Update page title based on route
  useEffect(() => {
    const path = location.pathname;
    const titles: Record<string, string> = {
      '/': 'Listings',
      '/listings': 'Listings',
      '/leads': 'Leads',
      '/jobs': 'Jobs',
      '/profiles': 'Profiles',
      '/system': 'System',
      '/settings': 'Settings',
    };
    
    // Handle dynamic routes
    let title = titles[path];
    if (!title) {
      if (path.startsWith('/listings/')) {
        title = 'Listing Details';
      } else if (path.startsWith('/leads/')) {
        title = 'Lead Details';
      } else if (path.startsWith('/jobs/')) {
        title = 'Job Details';
      } else {
        title = 'Page';
      }
    }
    
    setPageTitle(title);
    document.title = `${title} | WillScraper`;
    
    // Set breadcrumbs
    const segments = path.split('/').filter(Boolean);
    const breadcrumbs = segments.map((segment, index) => {
      const href = '/' + segments.slice(0, index + 1).join('/');
      const label = titles[href] || segment.charAt(0).toUpperCase() + segment.slice(1);
      return { label, href: index < segments.length - 1 ? href : undefined };
    });
    
    setBreadcrumbs(breadcrumbs);
  }, [location.pathname, setPageTitle, setBreadcrumbs]);
  
  // Check admin access if required
  useEffect(() => {
    if (requireAdmin && isInitialized && isAuthenticated) {
      // Check if user has admin role using the hook
      const user = useAuthStore.getState().user;
      const isAdmin = user?.role === 'admin';
      if (!isAdmin) {
        navigate('/', { replace: true });
      }
    }
  }, [requireAdmin, isInitialized, isAuthenticated, navigate]);
  
  // Show loading state while checking auth
  if (!isInitialized) {
    return (
      <div className="min-h-screen bg-[#ffffff] flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-2 border-[#000000] border-t-transparent" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-[#ffffff]">
      {/* Sidebar */}
      <Sidebar />
      
      {/* Main Content Area */}
      <div
        className={cn(
          'transition-all duration-300',
          'lg:ml-[280px]' // Sidebar width
        )}
      >
        {/* Header */}
        <Header />
        
        {/* Page Content */}
        <main className="min-h-screen">
          <ScrollArea className="h-[calc(100vh-64px)]">
            <PageWrapper className="p-6">
              {children || <Outlet />}
            </PageWrapper>
          </ScrollArea>
        </main>
      </div>
      
      {/* Toast Notifications */}
      <Toaster />
    </div>
  );
}
